import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { ProductCatalog } from '../../../models/product-catalog.model';
import { Order } from '../../../models/order.model';
import { MobileCheckServiceService } from '../../../services/mobile-check-service.service';
import { Product } from '../../../models/product.model';

export const PROGRAMS_ITEMS = 4;
export const PRODUCTS_ITEMS = 5;

/** The class names below are ours, not Swiper's, so none of Swiper's own control
 *  stylesheets are needed and nothing collides with the swiper-element instance the hero
 *  slider renders. AB#83916. */
export const BULLET_CLASS = 'app-carousel--bullet';
export const BULLET_ACTIVE_CLASS = 'app-carousel--bullet-active';
export const NAV_DISABLED_CLASS = 'app-carousel--nav-disabled';

/**
 * Deliberately still in PrimeNG's shape: `breakpoint` is a MAX width and the smallest
 * matching entry wins. Swiper expects the inverse. Keeping the tables byte-identical to
 * the pre-Swiper ones and converting them in one tested function is what makes the swap
 * auditable - rewriting the numbers by hand is where an off-by-one would hide. AB#83916.
 */
export interface ResponsiveOption {
  breakpoint: string;
  numVisible: number;
  numScroll: number;
}

const PROGRAMS_BREAKPOINTS: ResponsiveOption[] = [
  {
    breakpoint: '1290px',
    numVisible: 3,
    numScroll: 3,
  },
  {
    breakpoint: '998px',
    numVisible: 2,
    numScroll: 2,
  },
  {
    breakpoint: '675px',
    numVisible: 1,
    numScroll: 1,
  },
];

const PRODUCTS_BREAKPOINTS: ResponsiveOption[] = [
  {
    breakpoint: '1110px',
    numVisible: 4,
    numScroll: 4,
  },
  {
    breakpoint: '890px',
    numVisible: 3,
    numScroll: 3,
  },
  {
    breakpoint: '528px',
    numVisible: 2,
    numScroll: 2,
  },
];

export interface SwiperTier {
  slidesPerView: number;
  slidesPerGroup: number;
}

export interface SwiperResponsive {
  /** Applies below the first breakpoint - Swiper's top-level `slidesPerView`. */
  smallest: SwiperTier;
  breakpoints: Record<number, SwiperTier>;
}

/**
 * Invert a PrimeNG max-width ladder into a Swiper min-width one.
 *
 * PrimeNG applied each entry as `@media (max-width: <breakpoint>)`, so `675px -> 1` means
 * "1 item at 675px and below". Swiper's breakpoints are min-width, so the same boundary
 * is expressed from the other side as `676 -> 2`. The +1 is that, and nothing else.
 *
 *   programs, PrimeNG: base 4, <=1290 -> 3, <=998 -> 2, <=675 -> 1
 *   programs, Swiper : below 676 -> 1, >=676 -> 2, >=999 -> 3, >=1291 -> 4
 *
 * `base` is the widest tier, which <p-carousel> received as `[numVisible]="visibleItems"`
 * - already capped at the number of items.
 */
export function toSwiperBreakpoints(
  options: ResponsiveOption[],
  base: number
): SwiperResponsive {
  const ascending = [...options].sort(
    (a, b) => parseInt(a.breakpoint, 10) - parseInt(b.breakpoint, 10)
  );

  if (!ascending.length) {
    return {
      smallest: { slidesPerView: base, slidesPerGroup: base },
      breakpoints: {},
    };
  }

  const breakpoints: Record<number, SwiperTier> = {};
  ascending.forEach((option, index) => {
    // The tier that applies ABOVE this breakpoint is the next entry up, or `base` when
    // this is the widest entry.
    const above = ascending[index + 1];
    breakpoints[parseInt(option.breakpoint, 10) + 1] = above
      ? { slidesPerView: above.numVisible, slidesPerGroup: above.numScroll }
      : { slidesPerView: base, slidesPerGroup: base };
  });

  return {
    smallest: {
      slidesPerView: ascending[0].numVisible,
      slidesPerGroup: ascending[0].numScroll,
    },
    breakpoints,
  };
}

/**
 * True when two item arrays hold the same objects in the same order.
 *
 * The parents feed this component from getters that rebuild the array every time they are
 * read - `buyingWindowHomepageCatalogs.slice(0, count)` and
 * `onDemandHomepageCatalogs.slice(0, count).filter(...)`. Angular evaluates those on every
 * change-detection pass, so `items` arrives with a fresh reference constantly while its
 * contents never change. Treating a new reference as new data destroys and rebuilds the
 * carousel several times a second, and a rebuilt Swiper starts at slide 0 - which is why
 * pressing "next" appeared to snap straight back to the first page. AB#83916.
 */
export function sameItemIdentities(
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  return left.length === right.length && left.every((x, i) => x === right[i]);
}

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: Product[] | ProductCatalog[];
  @Input() order: Order;

  @ViewChild('swiperEl') swiperEl: ElementRef<HTMLElement>;
  @ViewChild('prevEl') prevEl: ElementRef<HTMLElement>;
  @ViewChild('nextEl') nextEl: ElementRef<HTMLElement>;
  @ViewChild('paginationEl') paginationEl: ElementRef<HTMLElement>;

  private swiper: Swiper | null = null;
  /** The exact objects the current slides were built from - see sameItemIdentities. */
  private renderedItems: readonly unknown[] = [];
  /** Handle for the deferred rebuild, so teardown can cancel it - see scheduleRebuild. */
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private mobileService: MobileCheckServiceService,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.buildSwiper();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['items'] || changes['items'].firstChange) {
      return;
    }
    // A new array reference is NOT new data here: the parents build these arrays inside
    // getters, so every change-detection pass hands over a fresh one.
    if (sameItemIdentities(this.renderedItems, changes['items'].currentValue)) {
      return;
    }
    this.scheduleRebuild();
  }

  ngOnDestroy(): void {
    this.cancelScheduledRebuild();
    this.destroySwiper();
  }

  private scheduleRebuild(): void {
    // The view still holds the previous slides while ngOnChanges runs. Rebuilding now
    // would have Swiper measure a stale DOM, so wait for Angular to render the new ones.
    //
    // The handle is kept because this callback outlives nothing on its own: `buildSwiper`
    // guards on `swiperEl.nativeElement`, but an ElementRef survives the component and
    // still points at its element once detached, so a timeout that fires after
    // ngOnDestroy would build a Swiper nobody can ever destroy - together with its
    // ResizeObserver and window listeners. Cancelling before rescheduling also collapses
    // a burst of input changes into one rebuild instead of one per change.
    this.cancelScheduledRebuild();
    this.rebuildTimer = setTimeout(() => {
      this.rebuildTimer = null;
      this.destroySwiper();
      this.buildSwiper();
    });
  }

  private cancelScheduledRebuild(): void {
    if (this.rebuildTimer !== null) {
      clearTimeout(this.rebuildTimer);
      this.rebuildTimer = null;
    }
  }

  get itemsCount(): number {
    return this.items?.length || 0;
  }

  get visibleItems(): number {
    const count = this.isProgramType ? PROGRAMS_ITEMS : PRODUCTS_ITEMS;
    return Math.min(this.itemsCount, count);
  }

  get isMobile(): boolean {
    return this.mobileService.mobileDevice;
  }

  get isTable(): boolean {
    return this.mobileService.tabletDevice;
  }

  get showNavigation(): boolean {
    return this.itemsCount > this.visibleItems;
  }

  get isProgramType(): boolean {
    if (!this.itemsCount) {
      return false;
    }
    return this.items[0] instanceof ProductCatalog;
  }

  get responsiveOptions(): ResponsiveOption[] {
    if (this.isProgramType) {
      return PROGRAMS_BREAKPOINTS;
    }
    return PRODUCTS_BREAKPOINTS;
  }

  private buildSwiper(): void {
    if (!this.swiperEl?.nativeElement || !this.itemsCount) {
      return;
    }

    const { smallest, breakpoints } = toSwiperBreakpoints(
      this.responsiveOptions,
      this.visibleItems
    );

    // Swiper installs Resize and Observer unconditionally (`use([Resize, Observer])` at
    // the end of swiper-core), so construction registers a ResizeObserver on the element
    // plus window listeners, and every one of their callbacks calls update(). Built
    // inside Angular's zone those callbacks each run a full change-detection pass over
    // the whole app, several times a second while images settle - with up to three
    // carousels on the home page. Nothing in this template is bound to anything Swiper
    // writes, so none of it needs the zone. AB#83916.
    this.renderedItems = this.items ?? [];

    this.zone.runOutsideAngular(() => {
      this.swiper = new Swiper(this.swiperEl.nativeElement, {
        modules: [A11y, Navigation, Pagination],
        slidesPerView: smallest.slidesPerView,
        slidesPerGroup: smallest.slidesPerGroup,
        breakpoints,
        // <p-carousel> put no gap between items and did not wrap around.
        spaceBetween: 0,
        loop: false,
        watchOverflow: true,
        // `observer`/`observeSlideChildren` are deliberately OFF. They put a
        // MutationObserver on every slide's subtree, and update() writes inline styles -
        // so each image that finishes loading mutates the DOM, which schedules an
        // update, which mutates the DOM again. Every call site sits behind an @if that
        // guarantees the items exist before this component is created, and a real change
        // of `items` rebuilds explicitly in ngOnChanges, so there is nothing left for an
        // observer to catch that is worth that cost.
        navigation: this.showNavigation
          ? {
              prevEl: this.prevEl?.nativeElement,
              nextEl: this.nextEl?.nativeElement,
              disabledClass: NAV_DISABLED_CLASS,
            }
          : false,
        pagination: this.showNavigation
          ? {
              el: this.paginationEl?.nativeElement,
              clickable: true,
              bulletClass: BULLET_CLASS,
              bulletActiveClass: BULLET_ACTIVE_CLASS,
              // Swiper's default bullet is a bare <span> - not focusable and with no
              // accessible name - while <p-carousel> rendered real indicator buttons.
              // `bulletElement: 'button'` would still leave them without type="button",
              // i.e. submit buttons inside any form, so the element is written out here.
              // A11y then adds tabindex and aria-current on top. The label says "page"
              // rather than Swiper's own "Go to slide {{index}}", because numScroll
              // equals numVisible: one bullet is a page of cards, not a single card.
              renderBullet: (index: number, className: string) =>
                `<button type="button" class="${className}" aria-label="Go to page ${
                  index + 1
                }"></button>`,
            }
          : false,
      });
    });
  }

  private destroySwiper(): void {
    // `deleteInstance` and `cleanStyles` both true: the element is reused on rebuild.
    this.zone.runOutsideAngular(() => this.swiper?.destroy(true, true));
    this.swiper = null;
  }
}
